<template>
  <div>
    <div class="mb-1">
      <div
        class="custom-search align-items-center d-flex justify-content-between mb-1"
      >
        <b-button v-b-modal.modal-new-user variant="primary">
          <feather-icon icon="PlusIcon" />
          {{ $t('userManagement.new') }}
        </b-button>
        <div>
          <b-form-group>
            <label>{{ $t('userManagement.search') }}:</label>
            <b-form-input
              v-model="filter_form_query.search"
              :placeholder="$t('general.search')"
              type="text"
              class="d-inline-block mb-0"
              @input="searchUsers"
            />
          </b-form-group>
        </div>
      </div>
    </div>

    <b-card no-body>
      <b-card-header>
        <h3>{{ $t('userManagement.users_table') }}</h3>
      </b-card-header>

      <!-- table -->

      <b-table
        id="table-list-debtors"
        sticky-header="600px"
        :fields="columns"
        :tbody-tr-class="['tableRowClass']"
        show-empty
        small
        :items="GET_USERS.results"
        @row-clicked="onRowClick"
      >
        <template #head()="props">
          <span>{{ $t(props.label) }}</span>
        </template>
        <template #empty>
          <div class="text-center">{{ $t('general.no_records_to_show') }}</div>
        </template>
        <template #cell(actions)="props">
          <div class="d-flex justify-content-around">
            <!-- <b-button
              size="sm"
              variant="warning"
              @click="$router.push(`view/${props.item.id}`)"
            >
              <feather-icon icon="EditIcon" />
            </b-button> -->
            <b-button
              size="sm"
              variant="danger"
              @click="deleteUser(props.item.id)"
            >
              <feather-icon icon="TrashIcon" />
            </b-button>
          </div>
        </template>

        <template #cell(image)="props">
          <b-avatar size="40" variant="light-primary" :src="props.item.photo" />
        </template>

        <template #cell(phone_number)="props">
          <span v-if="props.item.phone_number">
            {{ phoneFormat(props.item.phone_number) }}
          </span>
          <span v-else>
            {{ $t('clients.no_information') }}
          </span>
        </template>

        <template slot="table-row" slot-scope="props">
          <span
            v-if="props.column.field === 'username'"
            class="d-flex flex-column justify-content-between"
          >
            <b-link
              class="ml-25 text-secondary"
              @click="
                $router.push({
                  name: 'user-view',
                  params: {
                    id: props.item.id,
                  },
                })
              "
            >
              {{ props.row.username }}
            </b-link>
          </span>
        </template>
      </b-table>

      <!-- pagination -->
      <div class="d-flex justify-content-between flex-wrap mx-2 mb-2">
        <div class="d-flex align-items-center mb-0 mt-1">
          <span class="text-nowrap">
            {{ $t('pagination.showing_one_to') }}
          </span>
          <b-form-select
            v-model="filter_form_query.page_size"
            :options="[5, 10, 15]"
            class="mx-1"
            @input="refresh"
          />
          <span class="text-nowrap"
            >{{ $t('pagination.entries') }} ( {{ GET_USERS.count }} )
          </span>
        </div>
        <div>
          <b-pagination
            :value="1"
            :total-rows="GET_USERS.count"
            :per-page="filter_form_query.page_size"
            first-number
            last-number
            align="right"
            prev-class="prev-item"
            next-class="next-item"
            class="mt-1 mb-0"
            @change="changePage"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </div>
      </div>

      <b-modal
        id="modal-new-user"
        v-model="modal_new_user"
        centered
        size="lg"
        :title="$t('userManagement.add_new_user')"
        @show="openNewUserModal()"
      >
        <validation-observer ref="newUserValidation">
          <b-form @submit.prevent>
            <b-row>
              <b-col cols="12">
                <validation-provider
                  #default="{ errors }"
                  :name="$t('userManagement.username')"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('userManagement.username')"
                    label-for="v-username-new-user"
                  >
                    <b-form-input
                      id="v-username-new-user"
                      v-model="new_user.username"
                      :state="errors.length > 0 ? false : null"
                      name="v-username-new-user"
                      placeholder="Username"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col cols="6">
                <b-form-group>
                  <div class="d-flex justify-content-between">
                    <label for="v-password-new-user">{{
                      $t('userManagement.password')
                    }}</label>
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    :name="$t('generals.password')"
                    rules="required|min:4"
                  >
                    <b-input-group
                      class="input-group-merge"
                      :class="errors.length > 0 ? 'is-invalid' : null"
                    >
                      <b-form-input
                        id="v-password-new-user"
                        v-model="new_user.password"
                        :state="errors.length > 0 ? false : null"
                        class="form-control-merge"
                        :type="passwordFieldType"
                        name="v-password-new-user"
                        placeholder="············"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          class="cursor-pointer"
                          :icon="passwordToggleIcon"
                          @click="togglePasswordVisibility"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col cols="6">
                <b-form-group>
                  <div class="d-flex justify-content-between">
                    <label for="v-password-confirm-new-user">{{
                      $t('userManagement.confirm_password')
                    }}</label>
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    :name="$t('userManagement.password_confirm')"
                    :rules="
                      `required|${$t('userManagement.password_confirm')}:@${$t(
                        'generals.password'
                      )}`
                    "
                  >
                    <b-input-group
                      class="input-group-merge"
                      :class="errors.length > 0 ? 'is-invalid' : null"
                    >
                      <b-form-input
                        id="v-password-confirm-new-user"
                        v-model="new_user.password_confirm"
                        :state="errors.length > 0 ? false : null"
                        class="form-control-merge"
                        :type="passwordFieldType"
                        name="v-password-confirm-new-user"
                        placeholder="············"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          class="cursor-pointer"
                          :icon="passwordToggleIcon"
                          @click="togglePasswordVisibility"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
        <template #modal-footer>
          <div class="w-100">
            <b-button
              variant="secondary"
              size="sm"
              class="float-right"
              @click="modal_new_user = false"
            >
              {{ $t('userManagement.cancel') }}
            </b-button>
            <b-button
              variant="primary"
              size="sm"
              class="float-right mr-1"
              @click="saveNewUser"
            >
              {{ $t('userManagement.save') }}
            </b-button>
          </div>
        </template>
      </b-modal>
    </b-card>
  </div>
</template>

<script>
import {
  BAvatar,
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BRow,
  BCol,
  BButton,
  BForm,
  BLink,
  BInputGroupAppend,
  BInputGroup,
  BCard,
  BCardHeader,
  BTable,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required, email, password } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import { mapGetters, mapActions } from 'vuex'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import i18n from '@/libs/i18n'

extend(i18n.t('userManagement.password_confirm'), {
  params: ['target'],
  validate(value, { target }) {
    return value === target
  },
  message: i18n.t('userManagement.passwords_match_error'),
})

export default {
  components: {
    BCard,
    BCardHeader,
    BAvatar,
    BPagination,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BInputGroupAppend,
    BInputGroup,
    BRow,
    BCol,
    BButton,
    BLink,
    FeatherIcon,
    ValidationProvider,
    ValidationObserver,
    BTable,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      email,
      dir: false,
      required,
      password,
      modal_new_user: false,
      columns: [
        {
          label: 'ID',
          key: 'id',
          width: '70px',
          thClass: 'tableHeadClass',
        },
        {
          label: 'userManagement.avatar',
          key: 'image',
          thClass: 'tableHeadClass',
        },
        {
          label: 'userManagement.username',
          key: 'username',
          thClass: 'tableHeadClass',
        },
        {
          label: 'userManagement.phone_number',
          key: 'phone_number',
          thClass: 'tableHeadClass',
        },
        {
          label: 'userManagement.actions',
          key: 'actions',
          class: 'width-200',
          thClass: 'tableHeadClass',
        },
      ],
      new_user: {
        username: '',
        password: '',
        password_confirm: '',
        // roles: []
      },
      filter_form_query: {
        search: '',
        page: 1,
        page_size: 10,
      },
      delay: 300,
    }
  },
  computed: {
    ...mapGetters('users', ['GET_USERS']),
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },
  mounted() {
    this.FETCH_USERS(this.filter_form_query)
  },
  methods: {
    ...mapActions('users', [
      'FETCH_USERS',
      'EDIT_USER',
      'CREATE_USER',
      'SEARCH_USERS',
    ]),

    refresh() {
      this.FETCH_USERS(this.filter_form_query)
    },

    openNewUserModal() {
      this.new_user = {
        username: '',
        password: '',
        password_confirm: '',
        // roles: []
      }
    },

    phoneFormat(phoneNumberString) {
      const cleaned = `${phoneNumberString}`.replace(/\D/g, '')
      const match = cleaned.match(/^(998|)?(\d{2})(\d{3})(\d{2})(\d{2})$/)
      if (match) {
        const intlCode = match[1] ? '+998 ' : ''
        return [
          intlCode,
          '(',
          match[2],
          ') ',
          match[3],
          '-',
          match[4],
          '-',
          match[5],
        ].join('')
      }
      return null
    },

    onRowClick(params) {
      this.$router.push({
        name: 'user-view',
        params: {
          id: params.id,
        },
      })
    },

    async saveNewUser() {
      this.$refs.newUserValidation
        .validate()
        .then(async (success) => {
          if (success) {
            const profile = {
              language: 'uz',
              theme: '1',
            }
            const new_user = {
              username: this.new_user.username,
              password: this.new_user.password,
              profile,
            }
            this.CREATE_USER(new_user)
              .then((resp) => {
                this.$router
                  .push({ name: 'user-view', params: { id: resp.data.id } })
                  .then(() => {
                    this.$_successToast(this.$t('generals.added'))
                  })
              })
              .catch((err) => {
                if (err.response.data.username) {
                  this.$_errorToast(this.$t('userManagement.username_exists'))
                } else {
                  this.$_errorToast(this.$t('generals.error'))
                }
              })
          }
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              icon: 'AlertCircleIcon',
              variant: 'danger',
              text: 'Ошибка',
            },
          })
        })
    },

    changePage(value) {
      this.filter_form_query.page = value
      this.refresh()
    },

    searchUsers() {
      clearTimeout(this.delay)
      this.delay = setTimeout(() => {
        this.filter_form_query.page = 1
        this.refresh()
      }, 300)
    },

    async deleteUser(id) {
      this.$_showAreYouSureModal().then((res) => {
        if (res) {
          this.EDIT_USER({
            id,
            is_active: false,
          })
            .then(() => {
              this.$_successToast(this.$t('generals.deleted'))
              this.refresh()
            })
            .catch(() => {
              this.$_errorToast(this.$t('generals.error'))
            })
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
@import '@core/scss/vue/pages/page-auth.scss';

#table-list-debtors {
  border-collapse: separate;
  border-spacing: 0 1rem;
  background: #f2f2f2 !important;
}
.tableRowClass {
  background: white;
  transform: translateY(5px);
  transition: all 0.5s ease-in-out;
  text-align: center;
  box-shadow: 0 4px 20px 0 rgba(238, 2, 2, 0.05);
  cursor: pointer;
  // -webkit-box-shadow:  0 4px 20px 0px lightgrey;  /* Safari 3-4, iOS 4.0.2 - 4.2, Android 2.3+ */
  // -moz-box-shadow:     0 4px 20px 0px lightgrey;  /* Firefox 3.5 - 3.6 */
  // box-shadow:          0 4px 6px 0px lightgrey;
  td {
    border-top: 0;
    padding: 20px !important;
    &:first-child {
      padding-left: 25px !important;
      border-top-left-radius: 0.5rem;
      border-bottom-left-radius: 0.5rem;
    }

    &:last-child {
      border-top-right-radius: 0.5rem;
      border-bottom-right-radius: 0.5rem;
    }
  }
}

.tableRowClass:hover {
  transform: translateY(0);
  // -webkit-box-shadow:  0 4px 20px 0px lightgrey;  /* Safari 3-4, iOS 4.0.2 - 4.2, Android 2.3+ */
  // -moz-box-shadow:     0 4px 20px 0px lightgrey;  /* Firefox 3.5 - 3.6 */
  // box-shadow:          0 4px 6px 0px lightgrey;
}

.tableHeadClass {
  background: #f2f2f2 !important;
  padding: 10px !important;
  border: none !important;
  text-align: center;
}
.tableRowClass:hover {
  .tableTitle {
    color: #7367f0 !important;
  }
}
</style>
